import {
  Component,
  ComponentType,
  FormEvent,
  ReactNode,
  createContext
} from 'react';
import isDeepEqual from 'react-fast-compare';
import { isLocal } from '../environment';
import mergeObjectSchemas from './mergeObjectSchemas';
import {
  ErrorMessage,
  Errors,
  FieldBlurEvent,
  FieldChangeEvent,
  FieldProps,
  FormProps,
  ObjectSchema,
  Schema,
  Touched
} from './types';
import evaluateSchema from './validation/evaluateSchema';
import removeRedundantValues from './validation/removeRedundantValues';
import { getSchemaValues, orderProperties } from './validation/utils';
import validateSchema, { hasErrors } from './validation/validateSchema';

type State = {
  values: { [key: string]: unknown };
  touched: Touched<ObjectSchema>;
  errors: Errors<ObjectSchema>;
  // Todo: Make this a state machine or something enum-y
  submitting: boolean;
  submitted: boolean;
  submissionFailed: boolean;
  steps: Array<ObjectSchema>;
  submissionErrors: { [key: string]: ErrorMessage };
};

export type SubmitHandler = (state: {
  values: { [key: string]: unknown };
  errors: Errors<ObjectSchema>;
  isValid: boolean;
}) => Promise<any>;

type Props = {
  /**
   * Name of the form
   */
  name: string;
  /**
   * An object containing initial values for the form.
   * This may for example be data from sessionStorage.
   */
  initialValues: { [key: string]: unknown };
  /**
   * The base JSON Schema definition of the form.
   */
  definition: Array<ObjectSchema>;
  /**
   * The submit handler of the form.
   */
  onSubmit: SubmitHandler;
  /**
   * A callback that is called whenever a form value changes.
   * This can be used to save data in sessionStorage.
   */
  onFormValuesChanged?: (data: unknown) => void;
  /**
   * The form renderer React component.
   */
  component?: ComponentType<FormProps>;
  /**
   * Form render-callback.
   * Can be used instead of `component`, and will take presedence if both are given.
   */
  render?: (props: FormProps) => ReactNode;
  /**
   * Callback for form submission errors.
   */
  onFormSubmissionError?: <T extends Error>(
    error: T
  ) => Promise<{ [key: string]: ErrorMessage }>;
  /**
   * Mark the form as readOnly
   *
   * Change handlers will not do anything in this mode.
   */
  readOnly?: boolean;
  /**
   * Reset form when `initialValues` change (deep equality).
   *
   * Typically you do not want this since <Form /> controls all
   * state and you will lose user input. Use with caution.
   */
  enableReinitialize?: boolean;
};

export const FormDataContext = createContext<{ [key: string]: unknown }>({});

export default class Form extends Component<Props, State> {
  static defaultProps = {
    initialValues: {},
    enableReinitialize: false
  };

  mounted = false;

  constructor(props: Props) {
    super(props);

    const { steps, errors, values } = computeWorkingValues(
      this.props.definition,
      this.props.initialValues,
      false
    );

    this.state = {
      values,
      steps,
      touched: {},
      errors,
      submitting: false,
      submissionFailed: false,
      submitted: false,
      submissionErrors: {}
    };
  }

  componentDidMount() {
    this.mounted = true;

    if (this.props.onFormValuesChanged) {
      this.props.onFormValuesChanged(this.state.values);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (isLocal && this.props.definition !== prevProps.definition) {
      // eslint-disable-next-line no-console
      console.warn(
        `<Form /> received a new 'definition' prop (object reference changed).
        You should verify if this was intended.`
      );
    }

    if (
      this.state.values !== prevState.values ||
      this.props.definition !== prevProps.definition
    ) {
      this.setState(
        state => {
          const nextState = computeWorkingValues(
            this.props.definition,
            this.props.enableReinitialize &&
              !isDeepEqual(this.props.initialValues, prevProps.initialValues)
              ? this.props.initialValues
              : state.values,
            false
          );

          let stepsChanged = true;
          if (isDeepEqual(nextState.steps, state.steps)) {
            stepsChanged = false;
            nextState.steps = state.steps;
          }

          let valuesChanged = true;
          if (isDeepEqual(nextState.values, state.values)) {
            valuesChanged = false;
            nextState.values = state.values;
          }

          let errorsChanged = true;
          if (isDeepEqual(nextState.errors, state.errors)) {
            errorsChanged = false;
            nextState.errors = state.errors;
          }

          if (!stepsChanged && !valuesChanged && !errorsChanged) {
            return null;
          }

          return nextState;
        },
        () => {
          if (this.props.onFormValuesChanged) {
            this.props.onFormValuesChanged(this.state.values);
          }
        }
      );
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleChange = (e: FieldChangeEvent) => {
    if (this.props.readOnly) {
      return;
    }

    this.setState(prevState => ({
      values: {
        ...prevState.values,
        [e.target.name]: e.target.value
      }
    }));
  };

  handleBlur = (e: FieldBlurEvent) => {
    if (this.props.readOnly) {
      return;
    }

    this.setState(prevState => ({
      touched: {
        ...prevState.touched,
        [e.target.name]: e.target.touched
      }
    }));
  };

  handleResetTouched = (fields: Array<string>) => {
    this.setState(prevState => ({
      touched: {
        ...prevState.touched,
        ...fields.reduce<Touched<ObjectSchema>>((touched, field) => {
          touched[field] = undefined;
          return touched;
        }, {})
      }
    }));
  };

  handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    if (e.preventDefault) {
      e.preventDefault();
    }

    this.setState({
      submitting: true,
      submitted: false,
      submissionFailed: false
    });

    try {
      const { errors, values } = computeWorkingValues(
        this.props.definition,
        this.state.values,
        true
      );

      const isValid = !hasErrors(errors);

      await this.props.onSubmit({
        values,
        errors: this.state.errors,
        isValid
      });

      if (this.mounted) {
        this.setState({
          submitting: false,
          submitted: true,
          submissionFailed: false
        });
      }
    } catch (error) {
      let submissionErrors = {};

      if (this.props.onFormSubmissionError) {
        try {
          submissionErrors = await this.props.onFormSubmissionError(
            error as any
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }

      if (this.mounted) {
        this.setState({
          submitting: false,
          submitted: false,
          submissionFailed: true,
          submissionErrors
        });
      }
    }
  };

  setSubmissionFailed = (submissionFailed: boolean) => {
    this.setState({ submissionFailed });
  };

  mapDefinitionToFields = () => {
    return this.state.steps.map(step => {
      const { properties = {}, $uiOptions = {} } = step as ObjectSchema;
      const fields = orderProperties(
        Object.keys(properties),
        $uiOptions.propertyOrder
      ).reduce<{ [key: string]: FieldProps<Schema, any> }>(
        (prevFields, currentField) => {
          prevFields[currentField] = {
            schema: properties[currentField],
            value: this.state.values[currentField],
            id: `form-${this.props.name}-${currentField}`,
            name: currentField,
            formName: this.props.name,
            errors: this.state.errors[currentField],
            onChange: this.handleChange,
            onBlur: this.handleBlur,
            touched: this.state.touched[currentField],
            forceShowError: false,

            readOnly: this.props.readOnly,
            isFieldRequired: step.required?.includes(currentField)
          };

          return prevFields;
        },
        {}
      );

      return {
        schema: step,
        isValid: () => {
          return !Object.keys(fields).some(field =>
            hasErrors(fields[field].errors)
          );
        },
        resetTouched: () => this.handleResetTouched(Object.keys(fields)),
        fields
      };
    });
  };

  render() {
    const steps = this.mapDefinitionToFields();

    const formProps = {
      name: this.props.name,
      errors: this.state.errors,
      touched: this.state.touched,
      steps,
      values: this.state.values,
      onSubmit: this.handleSubmit,
      submitting: this.state.submitting,
      submissionFailed: this.state.submissionFailed,
      setSubmissionFailed: this.setSubmissionFailed,
      submitted: this.state.submitted,
      submissionErrors: this.state.submissionErrors,
      definition: this.props.definition,
      setValues: (values: { [key: string]: unknown }) =>
        this.setState(prevState => ({
          values: {
            ...prevState.values,
            ...values
          }
        }))
    };

    if (typeof this.props.render === 'function') {
      return (
        <FormDataContext.Provider value={this.state.values}>
          {this.props.render(formProps)}
        </FormDataContext.Provider>
      );
    }

    if (this.props.component) {
      const { component: FormComponent } = this.props;

      return (
        <FormDataContext.Provider value={this.state.values}>
          <FormComponent {...formProps} />
        </FormDataContext.Provider>
      );
    }

    throw new Error(
      'Either the render or the component prop must be provided to <Form />'
    );
  }
}

function computeWorkingValues(
  baseSchema: Array<ObjectSchema>,
  values: { [key: string]: unknown },
  filterRedundantValues: boolean
) {
  const steps = evaluateSchema(baseSchema, values);

  const workingSchema = mergeObjectSchemas(steps);

  // Apply new default values for properties that
  // became active as a result of evaluating the schema
  let newValues = getSchemaValues(workingSchema, values);
  if (filterRedundantValues) {
    newValues = removeRedundantValues(workingSchema, newValues);
  }

  const errors = validateSchema(
    workingSchema,
    newValues
  ) as Errors<ObjectSchema>;

  return {
    steps,
    values: newValues,
    errors
  };
}
